import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'

import { useAddNotification } from 'hooks/useAddNotification'
import { patch, post, put } from 'utils/http'

import { useCustomerRevealApiQuota } from './useCustomerRevealApiQuota'

export const useCustomerRevealApiKeyCreate = () => {
  const customerId = Number.parseInt(useCustomerId() ?? '', 10)
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  const {
    data: {
      requestsQuota: currentRequestsQuota,
      identifiedQuota: currentIdentifiedCuota,
      secretApiKey,
      publicApiKey,
      apiKey,
    } = {},
  } = useCustomerRevealApiQuota()

  const hasExistingKeys = !!(publicApiKey || secretApiKey || apiKey)

  const createRevealApiConfig = async (payload: any) => {
    const requestQuota = Number.parseInt(payload.requestQuota, 10)
    const identifiedQuota = Number.parseInt(payload.identifiedQuota, 10)
    const uniqueIdentifiedIpsQuota = Number.parseInt(payload.uniqueIdentifiedIpsQuota, 10)
    const { publicApiKeyDomainAllowlist } = payload

    if (hasExistingKeys || (currentRequestsQuota && currentIdentifiedCuota)) {
      // regenerate both key types and update quotas
      await put(`/management/reveal_api/customers/${customerId}/api-key/SecretApiKey`)
      await put(`/management/reveal_api/customers/${customerId}/api-key/PublicApiKey`)
      await patch(`/management/reveal_api/customers/${customerId}`, {
        publicApiKeyDomainAllowlist,
        requestQuota,
        identifiedQuota,
        uniqueIdentifiedIpsQuota,
      })
    } else {
      // no record in db (reveal-api->user_configs) so create it
      await post('/management/reveal_api/customers', {
        requestQuota,
        identifiedQuota,
        customerId,
        uniqueIdentifiedQuota: uniqueIdentifiedIpsQuota,
      })
      if (publicApiKeyDomainAllowlist?.length) {
        await patch(`/management/reveal_api/customers/${customerId}`, {
          publicApiKeyDomainAllowlist,
        })
      }
    }
  }

  const { mutateAsync, ...rest } = useMutation({
    mutationFn: createRevealApiConfig,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer', String(customerId)] })
      addNotification('Reveal API key has been generated')
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer', String(customerId)] })
      addNotification('Error while generating Reveal API key', 'error')
    },
  })

  return {
    createRevealApiKey: mutateAsync,
    ...rest,
  }
}
